// SC-741 §8 — WHERE / filter builder: shared model + client-side preview assembly.
// The SERVER (build-soql) is authoritative for the final quoting/escaping; this mirrors it so the
// SOQL panel can show a live preview.

export type FilterCombinator = 'AND' | 'OR';

export interface FilterCondition {
  kind: 'condition';
  id: string;
  field: string; // base object field API name
  type: string; // Salesforce field type (drives operators + quoting)
  operator: string;
  valueMode: 'literal' | 'token';
  value: string; // single literal / token name (single-value operators)
  values: string[]; // multi-value operators (IN / INCLUDES / …)
}

export interface FilterGroup {
  kind: 'group';
  id: string;
  combinator: FilterCombinator;
  children: FilterNode[];
}

export type FilterNode = FilterGroup | FilterCondition;

export type FieldCategory = 'string' | 'picklist' | 'multipicklist' | 'boolean' | 'number' | 'date';

export interface OperatorDef {
  value: string;
  label: string;
  multi?: boolean;
}

let idSeq = 0;
export function newId(): string {
  // App code (not a workflow script) — crypto.randomUUID is fine; fall back to a counter.
  try {
    return crypto.randomUUID();
  } catch {
    idSeq += 1;
    return `f${idSeq}`;
  }
}

export function newCondition(field = '', type = 'string'): FilterCondition {
  return {
    kind: 'condition',
    id: newId(),
    field,
    type,
    operator: '=',
    valueMode: 'literal',
    value: '',
    values: [],
  };
}
export function newGroup(combinator: FilterCombinator = 'AND'): FilterGroup {
  return { kind: 'group', id: newId(), combinator, children: [] };
}

export function fieldCategory(type: string): FieldCategory {
  const t = (type || '').toLowerCase();
  if (t === 'boolean') return 'boolean';
  if (t === 'multipicklist') return 'multipicklist';
  if (t === 'picklist') return 'picklist';
  if (['int', 'double', 'currency', 'percent', 'long'].includes(t)) return 'number';
  if (['date', 'datetime', 'time'].includes(t)) return 'date';
  return 'string';
}

export function operatorsFor(cat: FieldCategory): OperatorDef[] {
  switch (cat) {
    case 'boolean':
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
      ];
    case 'number':
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
        { value: '<', label: '<' },
        { value: '<=', label: '≤' },
        { value: '>', label: '>' },
        { value: '>=', label: '≥' },
        { value: 'IN', label: 'in', multi: true },
        { value: 'NOT IN', label: 'not in', multi: true },
      ];
    case 'date':
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
        { value: '<', label: '<' },
        { value: '<=', label: '≤' },
        { value: '>', label: '>' },
        { value: '>=', label: '≥' },
      ];
    case 'picklist':
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
        { value: 'IN', label: 'in', multi: true },
        { value: 'NOT IN', label: 'not in', multi: true },
      ];
    case 'multipicklist':
      return [
        { value: 'INCLUDES', label: 'includes', multi: true },
        { value: 'EXCLUDES', label: 'excludes', multi: true },
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
      ];
    default:
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
        { value: 'LIKE', label: 'like' },
        { value: 'IN', label: 'in', multi: true },
        { value: 'NOT IN', label: 'not in', multi: true },
      ];
  }
}

// String-like categories quote their values; number/date/boolean do not (date literals like
// LAST_N_DAYS:7 and ISO dates are unquoted in SOQL).
export function shouldQuote(cat: FieldCategory): boolean {
  return cat === 'string' || cat === 'picklist' || cat === 'multipicklist';
}

export function isMultiOperator(cat: FieldCategory, operator: string): boolean {
  return operatorsFor(cat).find((o) => o.value === operator)?.multi ?? false;
}

export const DATE_LITERALS = [
  'TODAY',
  'YESTERDAY',
  'TOMORROW',
  'THIS_WEEK',
  'LAST_WEEK',
  'NEXT_WEEK',
  'THIS_MONTH',
  'LAST_MONTH',
  'NEXT_MONTH',
  'THIS_QUARTER',
  'LAST_QUARTER',
  'THIS_YEAR',
  'LAST_YEAR',
  'LAST_N_DAYS:7',
  'LAST_N_DAYS:30',
  'NEXT_N_DAYS:7',
];

function quoteLiteral(v: string): string {
  return `'${v.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

function formatValue(c: FilterCondition): string {
  const cat = fieldCategory(c.type);
  const quote = shouldQuote(cat);
  if (isMultiOperator(cat, c.operator)) {
    const vals = c.values.filter((v) => v.trim() !== '');
    if (vals.length === 0) return '';
    return `(${vals.map((v) => (quote ? quoteLiteral(v) : v)).join(', ')})`;
  }
  const raw = (c.value ?? '').trim();
  if (raw === '') return '';
  if (c.valueMode === 'token') return quote ? `'[[${raw}]]'` : `[[${raw}]]`;
  return quote ? quoteLiteral(raw) : raw;
}

function conditionText(c: FilterCondition): string {
  if (!c.field || !c.operator) return '';
  const val = formatValue(c);
  if (val === '') return '';
  return `${c.field} ${c.operator} ${val}`;
}

function groupInner(g: FilterGroup): string {
  const parts = g.children
    .map((n) => (n.kind === 'group' ? wrapGroup(groupInner(n)) : conditionText(n)))
    .filter(Boolean);
  return parts.join(` ${g.combinator} `);
}
function wrapGroup(s: string): string {
  return s ? `(${s})` : '';
}

/** Top-level WHERE text (no outer parens); '' when nothing is filled in. */
export function buildWhereText(root: FilterGroup): string {
  return groupInner(root);
}

/** True when the tree has at least one complete (renderable) condition. */
export function hasFilters(root: FilterGroup): boolean {
  return buildWhereText(root) !== '';
}

// ── Immutable tree edits (by node id) ───────────────────────────────────────
export function updateById(
  group: FilterGroup,
  id: string,
  patch: (n: FilterNode) => FilterNode,
): FilterGroup {
  return {
    ...group,
    children: group.children.map((c) => {
      if (c.id === id) return patch(c);
      if (c.kind === 'group') return updateById(c, id, patch);
      return c;
    }),
  };
}

export function removeById(group: FilterGroup, id: string): FilterGroup {
  return {
    ...group,
    children: group.children
      .filter((c) => c.id !== id)
      .map((c) => (c.kind === 'group' ? removeById(c, id) : c)),
  };
}

export function addToGroup(group: FilterGroup, groupId: string, child: FilterNode): FilterGroup {
  if (group.id === groupId) return { ...group, children: [...group.children, child] };
  return {
    ...group,
    children: group.children.map((c) => (c.kind === 'group' ? addToGroup(c, groupId, child) : c)),
  };
}
